import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, GraduationCap, Shield, Building2, Chrome, Smartphone, HardDrive, Bot, ChevronDown, Briefcase, MessageSquare, Edit, Target, Zap, Search, Cpu, Lock, AlertTriangle, Wifi, Fingerprint, Eye, Key, Apple, Monitor, Terminal, Newspaper } from 'lucide-react';
import { mockCategories } from '../mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductionDropdownOpen, setIsProductionDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [isAIDropdownOpen, setIsAIDropdownOpen] = useState(false);
  const [isOSDropdownOpen, setIsOSDropdownOpen] = useState(false);
  const [isSecurityDropdownOpen, setIsSecurityDropdownOpen] = useState(false);
  const location = useLocation();

  const getCurrentCategory = () => {
    const path = location.pathname;
    if (path === '/') return null;
    const categoryId = path.split('/')[1];
    return mockCategories.find(cat => cat.id === categoryId);
  };

  const currentCategory = getCurrentCategory();

  // Production tools data (only core productivity tools)
  const productionTools = [
    { id: 'office365', name: 'Office 365', icon: Building2, description: 'Suite Microsoft complète' },
    { id: 'google-workspace', name: 'Google Workspace', icon: Chrome, description: 'Outils Google collaboratifs' },
    { id: 'synology', name: 'Synology', icon: HardDrive, description: 'NAS et stockage' }
  ];

  // Mobile devices data (separated from production tools)
  const mobileDevices = [
    { id: 'mobile', name: 'Apple', icon: Apple, description: 'iPhone, iPad, Mac et écosystème Apple' },
    { id: 'mobile', name: 'Android', icon: Bot, description: 'Smartphones et tablettes Android' }
  ];

  // Operating Systems data
  const operatingSystems = [
    { id: 'windows', name: 'Windows', icon: Monitor, description: 'Configuration et optimisation Windows' },
    { id: 'macos', name: 'macOS', icon: Apple, description: 'Productivité et sécurité sur Mac' },
    { id: 'linux', name: 'Linux', icon: Terminal, description: 'Administration et ligne de commande' }
  ];

  // Security domains data  
  const securityDomains = [
    { id: 'passwords', name: 'Mots de passe', icon: Lock, description: 'Sécurisation et gestion des identifiants' },
    { id: 'phishing', name: 'Anti-phishing', icon: AlertTriangle, description: 'Protection contre les tentatives d\'hameçonnage' },
    { id: 'wifi', name: 'Sécurité WiFi', icon: Wifi, description: 'Connexions réseau sécurisées' },
    { id: 'twofa', name: 'Authentification 2FA', icon: Fingerprint, description: 'Double authentification et sécurité renforcée' },
    { id: 'privacy', name: 'Vie privée', icon: Eye, description: 'Protection des données personnelles' },
    { id: 'backup', name: 'Sauvegarde', icon: Key, description: 'Protection et récupération des données' }
  ];

  // AI types data
  const aiTypes = [
    { id: 'conversational', name: 'IA Conversationnelles', icon: MessageSquare, description: 'ChatGPT, Claude, Gemini' },
    { id: 'creative', name: 'IA Créatives', icon: Edit, description: 'Midjourney, DALL-E' },
    { id: 'specialized', name: 'IA Spécialisées', icon: Target, description: 'Perplexity, Notion AI' },
    { id: 'productivity', name: 'IA Productivité', icon: Zap, description: 'Zapier, Motion' },
    { id: 'research', name: 'IA Recherche', icon: Search, description: 'Recherche académique' },
    { id: 'coding', name: 'IA Programmation', icon: Cpu, description: 'GitHub Copilot, Cursor' }
  ];
  
  // Dynamic header gradient based on current page
  const getHeaderGradient = () => {
    const path = location.pathname;
    
    // Special handling for IA type pages
    if (path.startsWith('/ia/')) {
      const aiType = path.split('/')[2];
      switch (aiType) {
        case 'conversational':
          return 'from-blue-600 via-blue-700 to-indigo-600';
        case 'creative':
          return 'from-pink-600 via-purple-600 to-rose-600';
        case 'specialized':
          return 'from-green-600 via-teal-600 to-emerald-600';
        case 'productivity':
          return 'from-orange-600 via-red-600 to-yellow-600';
        case 'research':
          return 'from-purple-600 via-indigo-600 to-blue-600';
        case 'coding':
          return 'from-cyan-600 via-blue-600 to-indigo-600';
        default:
          return 'from-indigo-600 via-purple-600 to-pink-600';
      }
    }
    
    if (!currentCategory) {
      return 'from-indigo-600 via-purple-600 to-cyan-600'; // Default for homepage
    }
    
    switch (currentCategory.theme) {
      case 'blue':
        return 'from-blue-600 via-blue-700 to-indigo-600';
      case 'emerald':
        return 'from-green-600 via-emerald-600 to-teal-600';
      case 'orange':
        return 'from-red-600 via-orange-600 to-yellow-600';
      case 'purple':
        return 'from-purple-600 via-pink-600 to-rose-600';
      case 'teal':
        return 'from-teal-600 via-cyan-600 to-blue-600';
      case 'indigo':
        return 'from-indigo-600 via-purple-600 to-pink-600';
      case 'slate':
        return 'from-slate-600 via-gray-600 to-blue-600';
      default:
        return 'from-indigo-600 via-purple-600 to-cyan-600';
    }
  };

  return (
    <header className={`bg-gradient-to-r ${getHeaderGradient()} sticky top-0 z-50 shadow-2xl backdrop-blur-sm transition-all duration-500`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center cursor-pointer group">
            <div className="bg-white bg-opacity-20 p-2 rounded-xl mr-3 group-hover:bg-opacity-30 transition-all duration-200">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">
              TICLIC.NET
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Outils de Production Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsProductionDropdownOpen(!isProductionDropdownOpen);
                  setIsMobileDropdownOpen(false);
                  setIsAIDropdownOpen(false);
                  setIsOSDropdownOpen(false);
                  setIsSecurityDropdownOpen(false);
                }}
                className="flex items-center px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 text-white hover:bg-white hover:bg-opacity-20 backdrop-blur-sm"
                title="Outils de production"
              >
                <Briefcase className="h-6 w-6" />
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>
              
              {/* Production Tools Dropdown */}
              {isProductionDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Outils de production</h3>
                    <div className="space-y-2">
                      {productionTools.map((tool) => {
                        const IconComponent = tool.icon;
                        return (
                          <Link
                            key={tool.id}
                            to={`/${tool.id}`}
                            onClick={() => setIsProductionDropdownOpen(false)}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                          >
                            <div className="p-2 rounded-lg bg-gray-100">
                              <IconComponent className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{tool.name}</p>
                              <p className="text-sm text-gray-500">{tool.description}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Appareils Mobiles Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsMobileDropdownOpen(!isMobileDropdownOpen);
                  setIsProductionDropdownOpen(false);
                  setIsAIDropdownOpen(false);
                  setIsOSDropdownOpen(false);
                  setIsSecurityDropdownOpen(false);
                }}
                className="flex items-center px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 text-white hover:bg-white hover:bg-opacity-20 backdrop-blur-sm"
                title="Appareils Mobiles"
              >
                <Smartphone className="h-6 w-6" />
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>
              
              {/* Mobile Devices Dropdown */}
              {isMobileDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Appareils Mobiles</h3>
                    <div className="space-y-2">
                      {mobileDevices.map((device, index) => {
                        const IconComponent = device.icon;
                        return (
                          <Link
                            key={index}
                            to={`/${device.id}`}
                            onClick={() => setIsMobileDropdownOpen(false)}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                          >
                            <div className="p-2 rounded-lg bg-gray-100">
                              <IconComponent className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{device.name}</p>
                              <p className="text-sm text-gray-500">{device.description}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Intelligence Artificielle Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsAIDropdownOpen(!isAIDropdownOpen);
                  setIsProductionDropdownOpen(false);
                  setIsMobileDropdownOpen(false);
                  setIsOSDropdownOpen(false);
                  setIsSecurityDropdownOpen(false);
                }}
                className="flex items-center px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 text-white hover:bg-white hover:bg-opacity-20 backdrop-blur-sm"
                title="Intelligence artificielle"
              >
                <Bot className="h-6 w-6" />
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>
              
              {/* AI Types Dropdown */}
              {isAIDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Intelligence artificielle</h3>
                    <div className="space-y-2">
                      {aiTypes.map((aiType) => {
                        const IconComponent = aiType.icon;
                        return (
                          <Link
                            key={aiType.id}
                            to={`/ia/${aiType.id}`}
                            onClick={() => setIsAIDropdownOpen(false)}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                          >
                            <div className="p-2 rounded-lg bg-gray-100">
                              <IconComponent className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{aiType.name}</p>
                              <p className="text-sm text-gray-500">{aiType.description}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Systèmes d'exploitation Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsOSDropdownOpen(!isOSDropdownOpen);
                  setIsProductionDropdownOpen(false);
                  setIsMobileDropdownOpen(false);
                  setIsAIDropdownOpen(false);
                  setIsSecurityDropdownOpen(false);
                }}
                className="flex items-center px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 text-white hover:bg-white hover:bg-opacity-20 backdrop-blur-sm"
                title="Systèmes OS"
              >
                <Monitor className="h-6 w-6" />
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>
              
              {/* OS Dropdown */}
              {isOSDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Systèmes d'exploitation</h3>
                    <div className="space-y-2">
                      {operatingSystems.map((os) => {
                        const IconComponent = os.icon;
                        const getOSUrl = (osId) => {
                          switch(osId) {
                            case 'windows': return '/operating-systems/guides/windows-11-optimization';
                            case 'macos': return '/operating-systems/guides/macos-productivity-security';
                            case 'linux': return '/operating-systems/guides/linux-administration-productivity';
                            default: return '/operating-systems';
                          }
                        };
                        
                        return (
                          <Link
                            key={os.id}
                            to={getOSUrl(os.id)}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                            onClick={() => setIsOSDropdownOpen(false)}
                          >
                            <div className="p-2 rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                              <IconComponent className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">{os.name}</p>
                              <p className="text-sm text-gray-500">{os.description}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sécurité Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsSecurityDropdownOpen(!isSecurityDropdownOpen);
                  setIsProductionDropdownOpen(false);
                  setIsMobileDropdownOpen(false);
                  setIsAIDropdownOpen(false);
                  setIsOSDropdownOpen(false);
                }}
                className="flex items-center px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 text-white hover:bg-white hover:bg-opacity-20 backdrop-blur-sm"
                title="Sécurité"
              >
                <Shield className="h-6 w-6" />
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>
              
              {/* Security Domains Dropdown */}
              {isSecurityDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Sécurité & Protection</h3>
                    <div className="space-y-2">
                      {securityDomains.map((domain) => {
                        const IconComponent = domain.icon;
                        return (
                          <Link
                            key={domain.id}
                            to="/security"
                            onClick={() => setIsSecurityDropdownOpen(false)}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                          >
                            <div className="p-2 rounded-lg bg-gray-100">
                              <IconComponent className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{domain.name}</p>
                              <p className="text-sm text-gray-500">{domain.description}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="bg-white bg-opacity-20 p-2 rounded-xl text-white hover:bg-opacity-30 transition-all duration-200"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-16 bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600 z-50 overflow-y-auto" style={{ height: 'calc(100vh - 4rem)' }}>
            <div className="space-y-3 pb-20">
              {/* Mobile Production Tools */}
              <div className="px-4">
                <h4 className="text-white text-lg font-bold mb-3 flex items-center">
                  <Briefcase className="h-5 w-5 mr-2" />
                  Outils de production
                </h4>
                <div className="space-y-2 ml-7">
                  {productionTools.map((tool) => {
                    const IconComponent = tool.icon;
                    return (
                      <Link
                        key={tool.id}
                        to={`/${tool.id}`}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                      >
                        <IconComponent className="h-4 w-4" />
                        <span className="text-sm">{tool.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Mobile AI Types */}
              <div className="px-4">
                <h4 className="text-white text-lg font-bold mb-3 flex items-center">
                  <Bot className="h-5 w-5 mr-2" />
                  Intelligence artificielle
                </h4>
                <div className="space-y-2 ml-7">
                  {aiTypes.map((aiType) => {
                    const IconComponent = aiType.icon;
                    return (
                      <Link
                        key={aiType.id}
                        to={`/ia/${aiType.id}`}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                      >
                        <IconComponent className="h-4 w-4" />
                        <span className="text-sm">{aiType.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Mobile Devices */}
              <div className="px-4">
                <h4 className="text-white text-lg font-bold mb-3 flex items-center">
                  <Smartphone className="h-5 w-5 mr-2" />
                  Appareils Mobiles
                </h4>
                <div className="space-y-2 ml-7">
                  <Link
                    to="/mobile/apple"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                  >
                    <Apple className="h-4 w-4" />
                    <span className="text-sm">Écosystème Apple</span>
                  </Link>
                  <Link
                    to="/mobile/android"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                  >
                    <Bot className="h-4 w-4" />
                    <span className="text-sm">Écosystème Android</span>
                  </Link>
                </div>
              </div>

              {/* Operating Systems */}
              <div className="px-4">
                <h4 className="text-white text-lg font-bold mb-3 flex items-center">
                  <Monitor className="h-5 w-5 mr-2" />
                  Systèmes OS
                </h4>
                <div className="space-y-2 ml-7">
                  {operatingSystems.map((os) => {
                    const IconComponent = os.icon;
                    const getOSUrl = (osId) => {
                      switch(osId) {
                        case 'windows': return '/operating-systems/guides/windows-11-optimization';
                        case 'macos': return '/operating-systems/guides/macos-productivity-security';
                        case 'linux': return '/operating-systems/guides/linux-administration-productivity';
                        default: return '/operating-systems';
                      }
                    };
                    
                    return (
                      <Link
                        key={os.id}
                        to={getOSUrl(os.id)}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                      >
                        <IconComponent className="h-4 w-4" />
                        <span className="text-sm">{os.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Security Domains */}
              <div className="px-4">
                <h4 className="text-white text-lg font-bold mb-3 flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  Sécurité & Protection
                </h4>
                <div className="space-y-2 ml-7">
                  <Link
                    to="/security"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                  >
                    <Shield className="h-4 w-4" />
                    <span className="text-sm">Guides Sécurité</span>
                  </Link>
                  {securityDomains.slice(0, 4).map((domain) => {
                    const IconComponent = domain.icon;
                    return (
                      <Link
                        key={domain.id}
                        to={`/security/guides/${domain.id === 'passwords' ? 'password-security' : domain.id === 'phishing' ? 'phishing-protection' : domain.id === 'wifi' ? 'wifi-security' : '2fa-authentication'}`}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                      >
                        <IconComponent className="h-4 w-4" />
                        <span className="text-sm">{domain.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;